import { KatalComponent, Keys } from '../../shared/base';

/**
 * @component {kat-tabs} KatalTabs Use tabs to enable customers an easy way to switch between different sections of information while staying on the same view.
 * @guideline Do Use tabs for organizing discrete blocks of information.
 * @guideline Do Use no more than seven tabs in a tab view to help maintain an uncluttered UI, and reduce cognitive load for customers.
 * @guideline Dont Don't use tabs as steps or navigation.
 * @guideline Dont Don’t hide error messages or critical information inside tabs.
 * @guideline Dont Don't nest tabs.
 * @guideline Dont Don't use tabs to  create flows.
 * @htmlExample - <kat-tab label="tab1" status-label="NEW" tab-id="1">Tab Content Goes Here</kat-tab>
 * @subcomponent ./tabs-tab.js
 * @contentProvided
 * @example SampleTabs {"selected-tab-id": "2", "content": "<kat-tab label=\"tab1\" status-label=\"NEW\" tab-id=\"1\">tab 1 content</kat-tab> <kat-tab label=\"tab2\" tab-id=\"2\">tab 2 content</kat-tab>"}
 * @event tab-change Fires when the selected tab has changed. The event detail contains selected-tab which is the target tab to display
 * @status Production
 * @theme flo
 */
export class KatalTabs extends KatalComponent {
  static get observedAttributes() {
    return ['selected-tab-id'];
  }

  constructor() {
    super(KatalTabs.observedAttributes);
    this._tabContainer = document.createElement('kat-tab-pane');
  }

  connectedCallback() {
    super.connectedCallback();

    const config = { childList: true, attributes: true, subtree: true };
    this._observer = new MutationObserver(mutations => {
      /* istanbul ignore next Don't know how to trigger a mutation from our current test setup. */
      this.domChange(mutations);
    });
    this._observer.observe(this, config);

    this.generateTabs();
    if (this.selectedTabId) {
      this.toggleTabs(this.selectedTabId);
    }
    this.addEventListener('click', event => {
      this.handleTabHeaderClick(event);
    });

    this.addEventListener('keydown', event => {
      if (event.keyCode === Keys.Enter || event.keyCode === Keys.Space) {
        this.handleTabHeaderClick(event);
      }
    });
  }

  handleTabHeaderClick(event) {
    const tabPane = event.target.closest('kat-tab-pane');
    //ensure this click was in the tabContainer
    if (!tabPane) {
      return;
    }

    //we want to work with the actual tab header that was clicked
    const tabElement = event.target.closest('kat-tab-header');
    const targetTab = tabElement.getAttribute('tab-id');
    if (targetTab === this.selectedTabId) {
      return;
    }
    this.selectedTabId = targetTab;
  }

  disconnectedCallback() {
    this._observer.disconnect();
  }

  domChange(mutations) {
    const validMutations = mutations.filter(mutation => {
      const isKatTab = node => {
        return node.tagName === 'KAT-TAB';
      };

      // TODO: Not sure how to unit test this filtering.
      // If any of the added or removed nodes are kat-tab, the mutation is valid
      // NodeList are not Arrays and are not filterable by default
      return (
        (mutation.addedNodes &&
          Array.from(mutation.addedNodes).some(isKatTab)) ||
        (mutation.removedNodes &&
          Array.from(mutation.removedNodes).some(isKatTab)) ||
        (mutation.target && isKatTab(mutation.target))
      );
    });

    if (validMutations.length > 0) {
      this.generateTabs();
    }
  }

  /**
   * @classprop {string} selected-tab-id The name of the tab currently being displayed
   * @required
   */
  get selectedTabId() {
    return this.getAttribute('selected-tab-id') || '';
  }

  set selectedTabId(value) {
    this.setAttribute('selected-tab-id', value);
    this.toggleTabs(value);
    this.dispatchCustomEvent('tab-change', { 'selected-tab-id': value }, false);
  }

  generateTabs() {
    // Render the tabs
    const tabs = this.querySelectorAll('kat-tab');
    const tabIdsAdded = {};

    //The tab container should only be placed one time
    if (!this.contains(this._tabContainer)) {
      this.prepend(this._tabContainer);
    }

    //for each tab add a new element to the tabContainer
    this._tabContainer.innerHTML = '';
    tabs.forEach(newTab => {
      const tabLabel = newTab.getAttribute('label');
      const statusLabel = newTab.getAttribute('status-label');
      const statusLabelText = statusLabel ? statusLabel : '';
      const tabId = newTab.getAttribute('tab-id');
      const tabSelected = tabId === this.selectedTabId;

      if (tabId === undefined || tabId === null) {
        console.warn(
          "Added a tab to KatalTabs with no tab-id, this won't work right"
        );
      } else if (tabIdsAdded[tabId]) {
        console.warn(
          `Already added ${tabIdsAdded[tabId]} tab(s) with tab-id '${tabId}', this probably won't work right`
        );
        tabIdsAdded[tabId]++;
      } else {
        tabIdsAdded[tabId] = 1;
      }

      // Create header element
      const tabHeaderElement = document.createElement('kat-tab-header');
      tabHeaderElement.title = tabLabel + ' ' + statusLabelText;
      tabHeaderElement.setAttribute('tab-id', tabId);
      tabHeaderElement.setAttribute('tabindex', '0');
      if (tabSelected) {
        tabHeaderElement.classList.add('tab-selected');
      }

      // Create header container div
      const tabHeaderContainerDiv = document.createElement('div');

      // Create label span
      const tabLabelSpan = document.createElement('span');
      tabLabelSpan.classList.add('katal-tab-label');
      tabLabelSpan.innerText = tabLabel;

      // Create status label span
      const tabStatusLabelSpan = document.createElement('span');
      tabStatusLabelSpan.classList.add('katal-tab-status-label');
      tabStatusLabelSpan.innerText = statusLabelText;

      // Add label span and status label span to container
      tabHeaderContainerDiv.appendChild(tabLabelSpan);
      tabHeaderContainerDiv.appendChild(tabStatusLabelSpan);

      // Add container to header element
      tabHeaderElement.appendChild(tabHeaderContainerDiv);

      this._tabContainer.appendChild(tabHeaderElement);
    });

    //If there is no tab set explicitly, default to the first tab in the list
    if (this.selectedTabId == '' && tabs.length > 0) {
      const firstTab = tabs[0];
      const firstTabId = firstTab.getAttribute('tab-id');
      if (firstTabId) {
        this.selectedTabId = firstTabId;
      }
    }

    this.toggleTabs(this.selectedTabId);
  }

  // Respond to attribute changes.
  attributeChangedCallback(attr, oldValue, newValue) {
    this.toggleTabs(newValue);
  }

  toggleTabs(newTabId) {
    this.selectTabContainer(newTabId);
    this.selectTabButton(newTabId);
  }

  selectTabContainer(newTabId) {
    this.selectTabElement(newTabId, 'kat-tab');
  }

  selectTabButton(newTabId) {
    this.selectTabElement(newTabId, 'kat-tab-header');
  }

  selectTabElement(newTabId, elementType) {
    if (!this.isReadyToRender()) {
      // Just skip this, we know when the element is attached it will be re-rendered correctly
      return;
    }

    const oldSelector = `${elementType}.tab-selected`;

    // Check if the correct tab is already selected, and if so bail.
    // This is not just a performance optimization, it also stops infinite loops when rendering.
    const oldSelectedEls = this.querySelectorAll(oldSelector);
    if (oldSelectedEls.length === 1) {
      const oldSelectedEl = oldSelectedEls[0];
      if (oldSelectedEl.getAttribute('tab-id') === newTabId) {
        // Right tab is already selected
        return;
      }
    }

    this.removeCssClassFromMatching(oldSelector, 'tab-selected');

    const newSelector = `${elementType}[tab-id='${newTabId}']`;
    const newlySelectedEl = this.querySelector(newSelector);

    if (newlySelectedEl) {
      newlySelectedEl.classList.add('tab-selected');
    } else if (newTabId) {
      this.throwIfDomLoaded(
        `Could not find element for selected ${elementType} with ID '${newTabId}'`
      );
    }
  }

  /**
   * Check if this object and the DOM are in a state where we can render.
   */
  isReadyToRender() {
    return this._propertiesHaveBeenUpgraded;
  }

  /**
   * We only want these errors to be thrown if we know the DOM is fully parsed.
   * Otherwise we could give false negatives to users of our components.
   */
  throwIfDomLoaded(error) {
    if (this.isDomLoaded()) {
      throw new Error(error);
    }
  }

  /**
   * Check if the DOM is loaded.
   *
   * @returns {boolean} Whether the DOM is currently loaded
   */
  isDomLoaded() {
    return document.readyState !== 'loading';
  }

  /**
   * Looks for elements matching the given CSS selector, and if any are found, removes the given CSS class from them.
   */
  removeCssClassFromMatching(selectors, cssClass) {
    const currentlySelectedAll = this.querySelectorAll(selectors);
    for (let i = 0; i < currentlySelectedAll.length; i++) {
      const currentlySelected = currentlySelectedAll[i];
      currentlySelected.classList.remove(cssClass);
    }
  }
}

KatalComponent.register('kat-tabs', KatalTabs);
